// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { SparseOrganization } from '@/account/api/types';
import type { UpdateOrganizationParameters } from '@/account/api/update-organization';
import {
  attentionThresholdRowsSchema,
  buildAttentionThresholdsInput,
  toAttentionThresholdRowsByKey,
} from '@/account/components/attention-threshold-form';
import { AttentionThresholdTable } from '@/account/components/attention-threshold-table';
import { OrganizationSerialPatternsSection } from '@/account/components/organization-serial-patterns-section';
import {
  useAttentionThresholds,
  useUpdateAttentionThresholds,
} from '@/account/hooks/use-attention-thresholds';
import { useUpdateOrganization } from '@/account/hooks/use-update-organization';
import { FormCard } from '@/components/form-card';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  ChecklistRtlOutlined,
  KeyboardReturnOutlined,
  ShoppingCartOutlined,
  WarningStatusIcon,
} from '@/constants/hardfin-standard-icons';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { sparseLocationSchema } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  defaultOriginLocation: sparseLocationSchema.nullable(),
  defaultReturnLocation: sparseLocationSchema.nullable(),
  defaultReceiveLocation: sparseLocationSchema.nullable(),
  thresholds: attentionThresholdRowsSchema,
});

type FormData = z.infer<typeof formSchema>;

interface LocationFieldConfig {
  name:
    | 'defaultOriginLocation'
    | 'defaultReturnLocation'
    | 'defaultReceiveLocation';
  header: string;
  label: string;
  description: string;
  icon: ReactElement;
}

/**
 * Default location fields in asset lifecycle order: goods arrive via
 * purchase orders, ship out via fulfillment orders, and come back via
 * return orders. Icons match the navigation entries for each order type.
 */
const LOCATION_FIELDS: LocationFieldConfig[] = [
  {
    name: 'defaultReceiveLocation',
    header: 'Receiving',
    label: 'Default receive location',
    description:
      'Used as a default destination for new purchase orders when none is specified',
    icon: <ShoppingCartOutlined fontSize="small" color="action" />,
  },
  {
    name: 'defaultOriginLocation',
    header: 'Fulfillment',
    label: 'Default origin location',
    description:
      'Used as a default origin for new fulfillment orders when none is specified',
    icon: <ChecklistRtlOutlined fontSize="small" color="action" />,
  },
  {
    name: 'defaultReturnLocation',
    header: 'Returns',
    label: 'Default return location',
    description:
      'Used as a default destination for new return orders when none is specified',
    icon: <KeyboardReturnOutlined fontSize="small" color="action" />,
  },
];

interface Props {
  organization: SparseOrganization;
}

export function OrganizationLogisticsCard(props: Props) {
  const updateOrganizationMutation = useUpdateOrganization();
  const updateThresholdsMutation = useUpdateAttentionThresholds();
  const thresholdsQuery = useAttentionThresholds(props.organization.key);

  const {
    clearErrors,
    control,
    formState,
    handleSubmit,
    reset,
    resetField,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      defaultOriginLocation: props.organization.defaultOriginLocation,
      defaultReturnLocation: props.organization.defaultReturnLocation,
      defaultReceiveLocation: props.organization.defaultReceiveLocation,
      thresholds: toAttentionThresholdRowsByKey(
        thresholdsQuery.data.thresholds
      ),
    },
  });

  const isMutationPending =
    updateOrganizationMutation.isPending || updateThresholdsMutation.isPending;
  const isSaveDisabled = isMutationPending || !formState.isDirty;

  const handleReset = () => {
    reset();
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);

    window.open(url, '_blank');
  };

  const handleValidSubmission: SubmitHandler<FormData> = async (data) => {
    try {
      const isAnyLocationDirty =
        Boolean(formState.dirtyFields.defaultOriginLocation) ||
        Boolean(formState.dirtyFields.defaultReturnLocation) ||
        Boolean(formState.dirtyFields.defaultReceiveLocation);

      if (isAnyLocationDirty) {
        const parameters: UpdateOrganizationParameters = {};

        if (formState.dirtyFields.defaultOriginLocation) {
          parameters.defaultOriginLocationId =
            data.defaultOriginLocation?.id ?? null;
        }

        if (formState.dirtyFields.defaultReturnLocation) {
          parameters.defaultReturnLocationId =
            data.defaultReturnLocation?.id ?? null;
        }

        if (formState.dirtyFields.defaultReceiveLocation) {
          parameters.defaultReceiveLocationId =
            data.defaultReceiveLocation?.id ?? null;
        }

        const response = await updateOrganizationMutation.mutateAsync({
          orgKey: props.organization.key,
          parameters,
        });
        // Commit the saved location values immediately so a later thresholds
        // failure leaves only the thresholds dirty for retry
        resetField('defaultOriginLocation', {
          defaultValue: response.organization.defaultOriginLocation,
        });
        resetField('defaultReturnLocation', {
          defaultValue: response.organization.defaultReturnLocation,
        });
        resetField('defaultReceiveLocation', {
          defaultValue: response.organization.defaultReceiveLocation,
        });
      }

      if (formState.dirtyFields.thresholds) {
        const updated = await updateThresholdsMutation.mutateAsync({
          orgKey: props.organization.key,
          input: buildAttentionThresholdsInput(
            data.thresholds,
            thresholdsQuery.data.thresholds
          ),
        });
        resetField('thresholds', {
          defaultValue: toAttentionThresholdRowsByKey(updated.thresholds),
        });
      }
    } catch {
      // The failed mutation's error state drives the snackbar; whatever did
      // not save stays dirty so the user can retry just that part.
    }
  };

  const handleSnackbarClose = () => {
    updateOrganizationMutation.reset();
    updateThresholdsMutation.reset();
  };

  return (
    <FormCard
      title="Logistics"
      isDirty={formState.isDirty}
      isDisabled={isSaveDisabled}
      onReset={handleReset}
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      <SupportSnackbar
        isOpen={
          updateOrganizationMutation.isError || updateThresholdsMutation.isError
        }
        message="An error occurred while updating logistics settings."
        onClose={handleSnackbarClose}
      />
      <Box padding={2}>
        <Grid container spacing={{ xs: 4, md: 3 }}>
          {LOCATION_FIELDS.map((field) => (
            <Grid item xs={12} md={4} key={field.name}>
              <Stack spacing={1} height="100%">
                <Stack direction="row" spacing={1} alignItems="center">
                  {field.icon}
                  <Typography variant="subtitle2">{field.header}</Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexGrow: 1 }}
                >
                  {field.description}
                </Typography>
                <FormLocationSearch
                  name={field.name}
                  label={field.label}
                  grouping={['inventory']}
                  control={control}
                  disabled={isMutationPending}
                  onCreate={handleCreateLocation}
                />
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Divider />
      <Box padding={2}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningStatusIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Attention thresholds</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Set organization-specific day counts for attention reasons. Leave
              a row blank to use Hardfin defaults; enter all three days to
              configure an organization override.
            </Typography>
          </Stack>
          <AttentionThresholdTable
            clearErrors={clearErrors}
            control={control}
            errors={formState.errors.thresholds}
            setValue={setValue}
            settings={thresholdsQuery.data.thresholds}
            disabled={isMutationPending}
          />
        </Stack>
      </Box>
      {props.organization.usesThirdPartyFulfillment && (
        <>
          <Divider />
          <OrganizationSerialPatternsSection
            organization={props.organization}
          />
        </>
      )}
    </FormCard>
  );
}
